import type { FormType, FormField } from '@/types/form';
import { SortableFieldItem } from './SortableFieldItem';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Plus } from 'lucide-react';

interface FormCanvasProps {
  form: FormType;
  selectedFieldId: string | null;
  draggedFieldId: string | null;
  onSelectField: (fieldId: string) => void;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onDeleteField: (fieldId: string) => void;
  onDuplicateField: (fieldId: string) => void;
}

export const FormCanvas: React.FC<FormCanvasProps> = ({
  form,
  selectedFieldId,
  draggedFieldId,
  onSelectField,
  onUpdateField,
  onDeleteField,
  onDuplicateField,
}) => {
  const sortedFields = [...form.fields].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="p-6 min-h-full bg-muted/30">
      <div className="max-w-2xl mx-auto">
        {/* Form Header */}
        <Card className="mb-6 border-2 border-dashed border-muted-foreground/20">
          <CardContent className="p-6">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">{form.name}</h1>
              {form.description && (
                <p className="text-muted-foreground">{form.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="capitalize">{form.category}</span>
                <span>•</span>
                <span>Version {form.version}</span>
                <span>•</span>
                <span className="capitalize">{form.status}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Fields */}
        <div className="space-y-4">
          {sortedFields.map((field) => (
            <SortableFieldItem
              key={field.id}
              field={field}
              isSelected={selectedFieldId === field.id}
              isDragging={draggedFieldId === field.id}
              onClick={() => onSelectField(field.id)}
              onUpdate={(updates) => onUpdateField(field.id, updates)}
              onDelete={() => onDeleteField(field.id)}
              onDuplicate={() => onDuplicateField(field.id)}
            />
          ))}

          {/* Empty State */}
          {sortedFields.length === 0 && (
            <Card className="border-2 border-dashed border-muted-foreground/20 bg-muted/10">
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Start Building Your Form
                </h3>
                <p className="text-muted-foreground mb-4">
                  Drag field types from the palette on the left to add them to your form.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Plus className="h-4 w-4" />
                  <span>Add your first field to get started</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Form Footer */}
        {sortedFields.length > 0 && (
          <Card className="mt-6 border-2 border-dashed border-muted-foreground/20">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-muted-foreground">
                Form submission will be handled according to your settings
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};